"use client"

import { useMemo, useState, useEffect } from "react"
import { createPortal } from "react-dom"
import { ChevronLeft, ChevronRight, X } from "lucide-react"
import { useBookingStore } from "@/store/booking-store"

function toYmd(d: Date) {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, "0")
  const day = String(d.getDate()).padStart(2, "0")
  return `${y}-${m}-${day}`
}

export default function CalendarModal({
  open,
  onClose,
  branchId,
  serviceId,
  initialDate,
}: {
  open: boolean
  onClose: () => void
  branchId: string
  serviceId: string
  initialDate: string
}) {
  const setBookingState = useBookingStore((state) => state.setBookingState)
  const today = useMemo(() => new Date(), [])
  const [cursor, setCursor] = useState<Date>(() => new Date(initialDate || Date.now()))
  const [mounted, setMounted] = useState(false)
  
  useEffect(() => {
    setMounted(true)
  }, [])

  const monthLabel = cursor.toLocaleString(undefined, { month: "long", year: "numeric" })
  const minMonthKey = `${today.getFullYear()}-${today.getMonth()}`
  const cursorKey = `${cursor.getFullYear()}-${cursor.getMonth()}`
  const disablePrev = cursorKey === minMonthKey

  function daysInMonth(year: number, month: number) {
    return new Date(year, month + 1, 0).getDate()
  }
  const grid: (Date | null)[] = (() => {
    const y = cursor.getFullYear()
    const m = cursor.getMonth()
    const first = new Date(y, m, 1)
    const last = daysInMonth(y, m)
    const startWeekday = first.getDay() === 0 ? 7 : first.getDay() // Mon=1..Sun=7
    const arr: (Date | null)[] = []
    for (let i = 1; i < startWeekday; i++) arr.push(null)
    for (let d = 1; d <= last; d++) arr.push(new Date(y, m, d))
    return arr
  })()

  function choose(d: Date) {
    const ymd = toYmd(d)
    setBookingState({ selectedDate: ymd, selectedSlot: null })
    onClose()
  }

  if (!open || !mounted) return null

  return createPortal(
    <div className="fixed inset-0 bg-black/15 backdrop-blur-sm z-[9999] flex items-center justify-center p-4 animate-in fade-in duration-200" onClick={onClose}>
      <div 
        className="w-full max-w-sm bg-white rounded-3xl shadow-2xl p-6 animate-in zoom-in-95 duration-200" 
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-bold text-gray-900">{monthLabel}</h3>
          <div className="flex items-center gap-1">
            <button 
              disabled={disablePrev} 
              onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} 
              className="p-2 rounded-full hover:bg-gray-100 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
            >
              <ChevronLeft className="w-5 h-5 text-gray-600" />
            </button>
            <button 
              onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} 
              className="p-2 rounded-full hover:bg-gray-100 transition-colors"
            >
              <ChevronRight className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>
        
        <div className="grid grid-cols-7 gap-1 text-center mb-2">
          {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((w) => (
            <div key={w} className="text-[11px] font-bold text-gray-400 uppercase tracking-wider py-2">{w}</div>
          ))}
        </div>
        
        <div className="grid grid-cols-7 gap-1 text-center">
          {grid.map((d, i) => {
            const isPast = d ? new Date(toYmd(d)) < new Date(toYmd(today)) : true
            const isfar = d ? new Date(toYmd(d)) > new Date(toYmd(new Date(today.getFullYear(), today.getMonth(), today.getDate()+6))) : false
            const isSunday = d ? d.getDay() === 0 : false
            const isToday = d && toYmd(d) === toYmd(today)
            const isSelected = d && toYmd(d) === initialDate
            
            return (
              <div key={i} className="aspect-square flex items-center justify-center">
                {d ? (
                  <button
                    disabled={isPast || isfar || isSunday}
                    onClick={() => !isPast && !isfar && !isSunday && choose(d)}
                    className={`
                      w-9 h-9 rounded-full flex items-center justify-center text-sm font-medium transition-all duration-200
                      ${isSelected 
                        ? "bg-pink-600 text-white shadow-md shadow-pink-200" 
                        : isToday 
                          ? "bg-pink-50 text-pink-600 font-bold" 
                          : "hover:bg-gray-100 text-gray-700"
                      }
                      ${(isPast || isfar || isSunday) ? "opacity-30 cursor-not-allowed hover:bg-transparent" : ""}
                    `}
                  >
                    {d.getDate()}
                  </button>
                ) : null}
              </div>
            )
          })}
        </div>
        
        <div className="mt-6 pt-4 border-t border-gray-100 flex justify-end">
          <button 
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}


